import React from 'react';
import { Award, Users, Heart, Sparkles, Shield, Star } from 'lucide-react';

const About = () => {
  return (
    <div className="pt-24 md:pt-28">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/3736396/pexels-photo-3736396.jpeg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <img 
              src="/fresha-award.jpeg" 
              alt="Fresha Highly Recommended 2025" 
              className="h-24 w-auto mx-auto"
            />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Our <span className="text-amber-600">Story</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            A sanctuary of beauty, elegance, and empowerment designed exclusively for women
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                <Heart className="w-8 h-8 text-amber-600 mr-3" />
                <span className="text-amber-600 font-semibold text-lg uppercase tracking-wide">
                  About Posh Signature
                </span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Where Every Woman Shines
              </h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  Posh Signature Beauty Salon was born from a simple yet powerful vision: 
                  to create a luxurious, private sanctuary where women can truly be themselves 
                  while experiencing world-class beauty treatments.
                </p>
                <p>
                  As a women-only establishment, we understand the importance of comfort, 
                  privacy, and personalized care. Our salon is more than just a beauty destination—it's 
                  a place where confidence is cultivated, beauty is celebrated, and every woman 
                  leaves feeling absolutely radiant.
                </p>
                <p>
                  With years of expertise in luxury beauty services, premium international 
                  products, and a team of certified professionals, we're committed to delivering 
                  excellence in every treatment, every time.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1616394584738-fc6e612e71b9"
                alt="Luxury facial treatment"
                className="rounded-2xl shadow-lg w-full h-64 object-cover"
              />
              <img
                src="https://images.pexels.com/photos/5659009/pexels-photo-5659009.jpeg"
                alt="Spa treatment"
                className="rounded-2xl shadow-lg w-full h-64 object-cover mt-8"
              />
              <img
                src="https://images.unsplash.com/photo-1632345031435-8727f6897d53"
                alt="Manicure service"
                className="rounded-2xl shadow-lg w-full h-64 object-cover -mt-8"
              />
              <img
                src="https://images.unsplash.com/photo-1684868268327-7e5590bcfbd6"
                alt="Bridal makeup"
                className="rounded-2xl shadow-lg w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-amber-600">Core Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Privacy First</h3>
              <p className="text-gray-600 leading-relaxed">
                Your comfort and privacy are our top priorities. Our women-only environment 
                ensures you can relax completely without any concerns.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <Star className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Excellence</h3>
              <p className="text-gray-600 leading-relaxed">
                We never compromise on quality. From products to service, we deliver 
                nothing but the best for our valued clients.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Personalized Care</h3>
              <p className="text-gray-600 leading-relaxed">
                Every client is unique. We take time to understand your needs and 
                customize treatments for the best results.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <Award className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Expertise</h3>
              <p className="text-gray-600 leading-relaxed">
                Our team consists of certified professionals with years of experience 
                in luxury beauty treatments and styling.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Premium Products</h3>
              <p className="text-gray-600 leading-relaxed">
                We exclusively use internationally recognized, high-quality beauty 
                brands that deliver exceptional results.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Community</h3>
              <p className="text-gray-600 leading-relaxed">
                We're building more than a salon—we're creating a community of 
                confident, empowered women who support each other.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-amber-600">Posh Signature?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              What makes us the preferred choice for discerning women
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Exclusive Women-Only Environment</h3>
                <p className="text-gray-600">
                  Experience complete privacy and comfort in our dedicated women-only salon space.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Certified Expert Team</h3>
                <p className="text-gray-600">
                  Our stylists and therapists are trained, certified, and continuously updated on latest trends.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Premium International Products</h3>
                <p className="text-gray-600">
                  We use only the finest beauty and skincare brands from around the world.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Hygienic & Safe</h3>
                <p className="text-gray-600">
                  Strict hygiene protocols and sterilization standards for your safety and peace of mind.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                5
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Personalized Consultations</h3>
                <p className="text-gray-600">
                  Every treatment begins with a detailed consultation to understand your unique needs.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                6
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Luxury Ambiance</h3>
                <p className="text-gray-600">
                  Step into a world of elegance with our beautifully designed, calming salon interiors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-600 via-pink-400 to-amber-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Experience the Posh Signature Difference
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Join hundreds of satisfied women who trust us with their beauty needs
          </p>
          <a
            href="/booking"
            className="inline-flex items-center px-10 py-5 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-all duration-300 font-bold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105"
          >
            Book Your First Visit
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
